// Feedback client — gathers a troubleshooting snapshot and POSTs the
// player's message to /api/feedback (a Pages Function that stores it
// in D1 and emails the maintainer via Resend when configured). The
// snapshot is shown to the player before sending — nothing leaves the
// machine unseen.

import { APP_VERSION } from '../config';
import { authHeader } from './auth';

/** Gather a troubleshooting snapshot: game version, browser, OS,
 *  display, GPU, locale. The server adds edge-verified ip / geo /
 *  user-agent on its side, so no /api/whoami round-trip is needed
 *  here anymore. */
export async function buildSnapshot(): Promise<string> {
  const nav = navigator as Navigator & { userAgentData?: { platform?: string } };
  const lines: string[] = [];
  lines.push(`version: settle ${APP_VERSION}`);
  lines.push(`browser: ${nav.userAgent}`);
  const platform = nav.userAgentData?.platform || nav.platform || 'unknown';
  lines.push(`os/platform: ${platform}`);
  lines.push(`language: ${nav.language}`);
  try {
    lines.push(`timezone: ${Intl.DateTimeFormat().resolvedOptions().timeZone}`);
  } catch { /* environments without Intl tz */ }
  lines.push(`screen: ${screen.width}x${screen.height} @ ${window.devicePixelRatio}x dpr`);
  lines.push(`viewport: ${window.innerWidth}x${window.innerHeight}`);
  const gpu = gpuRenderer();
  if (gpu) lines.push(`gpu: ${gpu}`);
  return lines.join('\n');
}

function gpuRenderer(): string | null {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2') ?? canvas.getContext('webgl');
    if (!gl) return null;
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    if (!ext) return null;
    return String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL));
  } catch {
    return null;
  }
}

export interface FeedbackResult { ok: boolean; error?: string }
export interface FeedbackImage { dataB64: string; type: 'image/jpeg' }

export async function submitFeedback(
  message: string, snapshot: string, image?: FeedbackImage | null,
): Promise<FeedbackResult> {
  try {
    const res = await fetch('/api/feedback', {
      method: 'POST',
      // Auth header (when signed in) puts the username in the email
      // subject: 'settle: user feedback from <username>'.
      headers: { 'content-type': 'application/json', ...authHeader() },
      body: JSON.stringify({ message, snapshot, version: APP_VERSION, image: image ?? undefined }),
    });
    const body = await res.json().catch(() => null) as FeedbackResult | null;
    if (!res.ok || !body) return { ok: false, error: body?.error ?? `http ${res.status}` };
    return body;
  } catch {
    return { ok: false, error: 'network error' };
  }
}

/** Downscale + re-encode a screenshot for attachment: longest edge
 *  capped at 1920px, jpeg q0.85, hard limit ~2.5MB binary after
 *  encoding. Returns base64 (no data: prefix) ready for the API. */
export async function prepareImage(file: File): Promise<FeedbackImage | { error: string }> {
  if (!file.type.startsWith('image/')) return { error: 'that file is not an image' };
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, 1920 / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const cx = canvas.getContext('2d');
    if (!cx) return { error: 'could not process the image' };
    cx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close();
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
    if (!blob) return { error: 'could not encode the image' };
    if (blob.size > 2_500_000) return { error: 'screenshot is too large even after compression' };
    const dataB64 = await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
      r.onerror = () => reject(new Error('read failed'));
      r.readAsDataURL(blob);
    });
    if (!dataB64) return { error: 'could not encode the image' };
    return { dataB64, type: 'image/jpeg' };
  } catch {
    return { error: 'could not process the image' };
  }
}
